import { useLocalStorage } from "@rehooks/local-storage";
import { useCallback, useState } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import useFiltersVariables from "components/Filters/useFiltersVariables";
import useOpenTofuMigrationSearchQueue from "shared/Stack/OpenTofuMigration/useSearchQueue";
import { SearchQueryRequestedPage } from "types/generated";

import { MIGRATION_QUEUE_FILTER_VARIABLES, MIGRATION_QUEUE_LIMIT_STORAGE_KEY } from "./constants";
import { OPEN_TOFU_MIGRATION_INITIAL_LIMIT } from "../constants";

const useOpenTofuMigrationSearchQueueEntries = () => {
  const { filterVariables } = useFiltersVariables(MIGRATION_QUEUE_FILTER_VARIABLES);
  const { searchInput: fullTextSearch, predicates, sortOptionFields: orderBy } = filterVariables;

  const [rowsPerPage] = useLocalStorage<number>(
    MIGRATION_QUEUE_LIMIT_STORAGE_KEY,
    OPEN_TOFU_MIGRATION_INITIAL_LIMIT
  );
  const first = rowsPerPage || OPEN_TOFU_MIGRATION_INITIAL_LIMIT;

  const [requestedPage, setRequestedPage] = useState<SearchQueryRequestedPage | null>(null);
  const [after, setAfter] = useState<string | null>(null);

  const resetKey = `${fullTextSearch}:${JSON.stringify(predicates)}:${JSON.stringify(orderBy)}`;
  const [previousResetKey, setPreviousResetKey] = useState(resetKey);

  if (previousResetKey !== resetKey) {
    setPreviousResetKey(resetKey);
    setRequestedPage(null);
    setAfter(null);
  }

  const { entities, hasData, endCursor, startCursor, error, loadingContent } =
    useOpenTofuMigrationSearchQueue({
      variables: {
        input: {
          predicates,
          fullTextSearch,
          orderBy,
          first,
          after,
          requestedPage,
        },
      },
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    });

  const loadPage = useCallback(
    ({
      cursor,
      requestedPage,
    }: {
      cursor: string | null;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      setAfter(cursor);
      setRequestedPage(requestedPage);
    },
    []
  );

  const isFiltered = !!fullTextSearch || predicates.length > 0;

  return {
    entities,
    error,
    loadingContent,
    loadPage,
    endCursor,
    startCursor,
    isPageEmpty: !loadingContent && hasData && !entities.length && !isFiltered,
    hasNoFilteringResults: !loadingContent && hasData && !entities.length && isFiltered,
    paginationResetKey: { fullTextSearch, predicates, orderBy },
  };
};

export default useOpenTofuMigrationSearchQueueEntries;
